import { ReactComponent as AiIcon } from '../../assets/tabs/settings-tab/ai-icon.svg';
import { ReactComponent as BellIcon } from '../../assets/tabs/settings-tab/bell-icon.svg';
import { ReactComponent as BookmarkIcon } from '../../assets/tabs/settings-tab/bookmark-icon.svg';
import { ReactComponent as CalendarIcon } from '../../assets/tabs/settings-tab/calendar-icon.svg';
import { ReactComponent as CalendlyIcon } from '../../assets/tabs/settings-tab/calendly-icon.svg';
import { ReactComponent as ChromeIcon } from '../../assets/tabs/settings-tab/chrome-icon.svg';
import { ReactComponent as ClockIcon } from '../../assets/tabs/settings-tab/clock-icon.svg';
import { ReactComponent as EmailIcon } from '../../assets/tabs/settings-tab/email-icon.svg';
import { ReactComponent as EnergyIcon } from '../../assets/tabs/settings-tab/energy-icon.svg';
import { ReactComponent as FaqIcon } from '../../assets/tabs/settings-tab/faq-icon.svg';
import { ReactComponent as FriendsIcon } from '../../assets/tabs/settings-tab/friends-icon.svg';
import { ReactComponent as GeneralIcon } from '../../assets/tabs/settings-tab/general-icon.svg';
import { ReactComponent as MobileIcon } from '../../assets/tabs/settings-tab/mobile-icon.svg';
import { ReactComponent as ProfileIcon } from '../../assets/tabs/settings-tab/profile-icon.svg';
import { ReactComponent as TodoIcon } from '../../assets/tabs/settings-tab/todo-icon.svg';
import { ReactComponent as WatchIcon } from '../../assets/tabs/settings-tab/watch-icon.svg';

export const SETTING_ICONS = {
  ai: AiIcon,
  bell: BellIcon,
  bookmark: BookmarkIcon,
  calendar: CalendarIcon,
  calendly: CalendlyIcon,
  chrome: ChromeIcon,
  clock: ClockIcon,
  email: EmailIcon,
  energy: EnergyIcon,
  faq: FaqIcon,
  friends: FriendsIcon,
  general: GeneralIcon,
  mobile: MobileIcon,
  profile: ProfileIcon,
  todo: TodoIcon,
  watch: WatchIcon,
} as const;

export interface SettingsSectionItem {
  key: string;
  title: string;
  titleKey: string;
  components: SettingsSectionItemComponent[];
}

export interface SettingsSectionItemComponent {
  key: string;
  title: string;
  titleKey: string;
  icon?: React.ElementType<{ className?: string }>;
  section?: SettingsSectionItemComponent[];
}

export const settingsSections: SettingsSectionItem[] = [
  {
    key: 'account',
    title: 'Account',
    titleKey: 'settings.nav.account',
    components: [
      {
        key: 'profile',
        title: 'Profile',
        titleKey: 'settings.nav.profile',
        icon: SETTING_ICONS.profile,
      },
      {
        key: 'calendars',
        title: 'Calendars',
        titleKey: 'settings.nav.calendars',
        icon: SETTING_ICONS.calendar,
        section: [
          {
            key: 'defaultCalendar',
            title: 'Default Calendar',
            titleKey: 'settings.nav.defaultCalendar',
          },
          {
            key: 'eventAvailability',
            title: 'Event Availability',
            titleKey: 'settings.nav.eventAvailability',
          },
          {
            key: 'externalCalendarSync',
            title: 'External Calendar Sync',
            titleKey: 'settings.nav.externalCalendarSync',
          },
          {
            key: 'calendarVisibility',
            title: 'Calendar Visibility',
            titleKey: 'settings.nav.calendarVisibility',
            section: [
              {
                key: 'calendarVisibilityCompletedTasks',
                title: 'Completed Tasks',
                titleKey: 'settings.nav.completedTasks',
              },
              {
                key: 'calendarVisibilityRoutine',
                title: 'Routine',
                titleKey: 'settings.nav.routine',
              },
              {
                key: 'calendarVisibilityExercise',
                title: 'Exercise',
                titleKey: 'settings.nav.exercise',
              },
            ],
          },
        ],
      },
      {
        key: 'general',
        title: 'General',
        titleKey: 'settings.nav.general',
        icon: SETTING_ICONS.general,
      },
      {
        key: 'wearables',
        title: 'Health Data',
        titleKey: 'settings.nav.healthData',
        icon: SETTING_ICONS.watch,
      },
      {
        key: 'todos',
        title: 'Todos',
        titleKey: 'settings.nav.todos',
        icon: SETTING_ICONS.todo,
      },
      {
        key: 'energyPreferences',
        title: 'Energy Preferences',
        titleKey: 'settings.nav.energyPreferences',
        icon: SETTING_ICONS.energy,
      },
      {
        key: 'email',
        title: 'Email',
        titleKey: 'settings.nav.email',
        icon: SETTING_ICONS.email,
        section: [
          {
            key: 'emailReport',
            title: 'Report',
            titleKey: 'settings.email.report.title',
          },
        ],
      },
      {
        key: 'manageFriends',
        title: 'Friends',
        titleKey: 'settings.nav.friends',
        icon: SETTING_ICONS.friends,
        section: [
          {
            key: 'connectedFriends',
            title: 'Connected Friends',
            titleKey: 'settings.nav.connectedFriends',
          },
          {
            key: 'acceptInvite',
            title: 'Accept an invite',
            titleKey: 'settings.nav.acceptInvite',
          },
          {
            key: 'shareInvite',
            title: 'Share your invite',
            titleKey: 'settings.nav.shareInvite',
          },
          {
            key: 'shareFeedback',
            title: 'Share your feedback',
            titleKey: 'settings.nav.shareFeedback',
          },
        ],
      },
      {
        key: 'subscription',
        title: 'Subscription',
        titleKey: 'settings.nav.subscription',
        icon: SETTING_ICONS.bookmark,
      },
    ],
  },
  {
    key: 'feature',
    title: 'Feature',
    titleKey: 'settings.nav.feature',
    components: [
      {
        key: 'aiAssistant',
        title: 'AI Assistant',
        titleKey: 'settings.nav.aiAssistant',
        icon: SETTING_ICONS.ai,
      },
      {
        key: 'calendly',
        title: 'Calendly',
        titleKey: 'settings.nav.calendly',
        icon: SETTING_ICONS.calendly,
      },
    ],
  },
  {
    key: 'about',
    title: 'About',
    titleKey: 'settings.nav.about',
    components: [
      {
        key: 'helpFaq',
        title: 'Help & FAQ',
        titleKey: 'settings.nav.helpFaq',
        icon: SETTING_ICONS.faq,
      },
      {
        key: 'productHub',
        title: 'Product Hub',
        titleKey: 'settings.nav.productHub',
        icon: SETTING_ICONS.mobile,
      },
    ],
  },
];

const getSectionParentKeyMap = (sections: SettingsSectionItem[]) => {
  const sectionParentKeyMap: Record<string, string> = {};

  sections.forEach((topParent) => {
    topParent.components.forEach((subParent) => {
      sectionParentKeyMap[subParent.key] = subParent.key;

      const addSectionParentKey = (section: SettingsSectionItemComponent) => {
        sectionParentKeyMap[section.key] = subParent.key;
        section.section?.forEach(addSectionParentKey);
      };

      subParent.section?.forEach(addSectionParentKey);
    });
  });

  return sectionParentKeyMap;
};

export const SETTINGS_SECTION_PARENT_MAP = getSectionParentKeyMap(settingsSections);

export const SETTINGS_SUB_SECTION_PARENT_MAP: Record<string, string> = {
  defaultCalendar: 'calendars',
  eventAvailability: 'calendars',
  externalCalendarSync: 'calendars',
  calendarVisibility: 'calendars',
  calendarVisibilityCompletedTasks: 'calendars',
  calendarVisibilityRoutine: 'calendars',
  calendarVisibilityExercise: 'calendars',
  emailReport: 'email',
  locationSettings: 'aiAssistant',
  connectedFriends: 'manageFriends',
  acceptInvite: 'manageFriends',
  shareInvite: 'manageFriends',
  shareFeedback: 'manageFriends',
};
